import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User, Organization } from 'types';
import { authService } from 'api/services/auth.service';
import { getUserData, setUserData, clearStorage } from 'utils/storage';

interface AuthContextType {
  user: User | null;
  currentOrganization: Organization | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  /**
   * Establish a session for a user authenticated SOMEWHERE ELSE — today, by Google,
   * Microsoft or ChatGPT via the redirect flow. Performs no authentication itself.
   */
  establishSession: (user: User) => boolean;
  logout: () => void;
  updateUser: (user: User) => void;
  switchOrganization: (organization: Organization) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

/** The organisation the switcher saved, if it is the one the stored id names. */
export function readStoredOrganization(id: string): Organization | undefined {
  try {
    const raw = localStorage.getItem('currentOrganization');
    if (!raw) return undefined;
    const org = JSON.parse(raw);
    return org && org.id === id ? (org as Organization) : undefined;
  } catch {
    return undefined;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = getUserData();

    if (storedUser) {
      setUser(storedUser);

      // Set default organization
      const storedOrgId = localStorage.getItem('currentOrganizationId');

      if (storedUser.organizations && storedUser.organizations.length > 0) {
        // User has organizations array
        if (storedOrgId) {
          const org = storedUser.organizations.find((o: Organization) => o.id === storedOrgId)
            // A business reached through a provider is not a membership, so it is
            // not in user.organizations. The switcher saved it whole; restore that,
            // or the screen showed the provider while every request still went out
            // as the business (X-Organization-Id is read from localStorage).
            ?? readStoredOrganization(storedOrgId);
          if (org) {
            setCurrentOrganization(org);
          } else {
            setCurrentOrganization(storedUser.organizations[0]);
            localStorage.setItem('currentOrganizationId', storedUser.organizations[0].id);
          }
        } else {
          setCurrentOrganization(storedUser.organizations[0]);
        }
      } else if (storedUser.role) {
        // Fallback: No organizations array, but has user.role
        console.log('⚠️ Stored user has no organizations - using user.role:', storedUser.role);
        const syntheticOrg = {
          id: 'synthetic-org',
          name: storedUser.user_type === 'service' ? 'Service Provider' : 'Business Organization',
          type: storedUser.user_type || 'service',
          role: storedUser.role,
          business_id: storedUser.business_id
        } as Organization;
        setCurrentOrganization(syntheticOrg);
      }
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    const response = await authService.login({ username, password });

    if (response.success && response.data) {
      return establishSession(response.data.user);
    }

    return false;
  };

  /**
   * Everything that happens once a session exists, whoever established it.
   *
   * <p>Password login and social sign-in differ only in how the token was obtained;
   * from here they are identical — set the user, pick a default organisation, fall
   * back to a synthetic one for a service user with no organisations. Written twice,
   * the copy that drifts is the organisation selection, and a social user would land
   * on a portal with no current organisation and every page reporting none.
   */
  const establishSession = (userData: User): boolean => {

      // Debug: Log user data structure
      console.log('👤 User Data Structure:', {
        hasFirstName: !!userData.firstName,
        hasLastName: !!userData.lastName,
        hasName: !!userData.name,
        firstName: userData.firstName,
        lastName: userData.lastName,
        name: userData.name,
        organizationsCount: userData.organizations?.length || 0,
        firstOrgRole: userData.organizations?.[0]?.role,
        userRole: userData.role,
        userType: userData.user_type
      });

      setUser(userData);
      setUserData(userData);

      // Set default organization after login
      if (userData.organizations && userData.organizations.length > 0) {
        const defaultOrg = userData.organizations[0];
        console.log('🏢 Setting Default Organization:', {
          id: defaultOrg.id,
          name: defaultOrg.name,
          type: defaultOrg.type,
          role: defaultOrg.role
        });
        setCurrentOrganization(defaultOrg);
        localStorage.setItem('currentOrganizationId', defaultOrg.id);
      } else if (userData.role) {
        // Fallback: If no organizations array but user has a role, create a synthetic organization
        console.log('⚠️ No organizations array - creating synthetic organization from user.role:', userData.role);
        const syntheticOrg = {
          id: 'synthetic-org',
          name: userData.user_type === 'service' ? 'Service Provider' : 'Business Organization',
          type: userData.user_type || 'service',
          role: userData.role,
          business_id: userData.business_id
        } as Organization;
        setCurrentOrganization(syntheticOrg);
        localStorage.setItem('currentOrganizationId', syntheticOrg.id);
      } else {
        console.warn('⚠️ No organizations and no user.role found!');
      }

    return true;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setCurrentOrganization(null);
    clearStorage();
    localStorage.removeItem('currentOrganizationId');
    localStorage.removeItem('currentOrganization');
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    setUserData(updatedUser);
  };

  const switchOrganization = (organization: Organization) => {
    setCurrentOrganization(organization);
    localStorage.setItem('currentOrganizationId', organization.id);
    try { localStorage.setItem('currentOrganization', JSON.stringify(organization)); } catch { /* storage full/blocked */ }
    // Reload page to reset state for new organization
    window.location.reload();
  };

  const value: AuthContextType = {
    user,
    currentOrganization,
    isAuthenticated: !!user,
    loading,
    login,
    establishSession,
    logout,
    updateUser,
    switchOrganization
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};